import SubmitButton from "Components/Buttons/SubmitButton";
import { useFormik } from "formik";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addBrand, updateBrand } from "slices/brand/thunk";
import * as Yup from "yup";

const BrandForm: React.FC = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const handleBack = useHandleBack("/brand");

  const isEdit = Boolean(id);

  const brandList = useSelector((state: any) => state.Brand?.brandList || []);

  const [initialData, setInitialData] = useState({
    brandName: "",
    remark: "",
  });

  useEffect(() => {
    if (isEdit && state?.brandData) {
      setInitialData({
        brandName: state.brandData.brand_name || "",
        remark: state.brandData.remark || "",
      });
    }
  }, [id, isEdit, state]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      brandName: initialData.brandName,
      remark: initialData.remark,
    },
    validationSchema: Yup.object({
      brandName: Yup.string()
        .required("Please enter Brand Name")
        .trim()
        .max(255, "Brand Name must not exceed 255 characters"),
      remark: Yup.string()
        .max(500, "Remark must not exceed 500 characters")
        .nullable(),
    }),

    onSubmit: async (values) => {
      const payload = {
        brand_name: values.brandName.trim(),
        remark: values.remark?.trim() || null,
      };

      try {
        if (isEdit) {
          await dispatch(updateBrand({ id: id!, ...payload })).unwrap();
          toast.success("Brand updated successfully");
        } else {
          await dispatch(addBrand(payload)).unwrap();
          toast.success("Brand added successfully");
        }
        handleBack();
      } catch (error: any) {
        const errorMessage =
          error?.errors?.brand_name?.[0] ||
          error?.message ||
          "Something went wrong.";
        if (
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already exists")
        ) {
          toast.error("Brand name already exists");
        } else {
          toast.error(errorMessage);
        }
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">
                  {isEdit ? "Edit Brand" : "Add Brand"}
                </h5>
              </div>

              <div className="text-muted">
                <Link
                  to="/brand"
                  className="text-muted"
                  style={{ fontSize: "0.9rem" }}
                >
                  Brand
                </Link>{" "}
                &gt;{" "}
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  {isEdit ? "Edit Brand" : "Add Brand"}
                </span>
              </div>
            </div>

            <Col lg={12}>
              <div className="mb-3">
                <Form.Label>
                  Brand Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="brandName"
                  placeholder="Enter Brand Name"
                  value={formik.values.brandName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.brandName && formik.touched.brandName
                  }
                  style={{
                    height: "40px",
                    lineHeight: "1.5",
                    fontSize: "0.875rem",
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.brandName}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="remark"
                  placeholder="Enter Remark (Optional)"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.remark && formik.touched.remark}
                  style={{
                    fontSize: "0.875rem",
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remark}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <Button
              variant="outline-secondary"
              style={{
                color: "#878899",
                borderColor: "#B0BAC9",
                backgroundColor: "transparent",
              }}
              onClick={handleBack}
            >
              Cancel
            </Button>

            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default BrandForm;
